import React, { useRef, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import api from '../services/api';

export type NokeStockLocation = 'office' | 'store';
export interface NokeLocationRow {
  variant_id: number;
  sku: string;
  product_title: string;
  color: string;
  size: string;
  qty_office?: number;
  qty_in_transit?: number;
  qty_on_hand: number;
  on_display?: boolean;
  storage_slot?: string | null;
}
type TransferAction = 'dispatch' | 'receive' | 'return_to_office';
interface TransferRequest {
  action: TransferAction;
  items: { variant_id: number; qty: number }[];
  client_ref: string;
}
const actions: Record<TransferAction, { label: string; source: string; description: string }> = {
  dispatch: {
    label: '送往專櫃', source: '辦公室',
    description: '填入這次實際送出的數量。送出後轉為運送中，尚未計入在櫃庫存。',
  },
  receive: {
    label: '確認收貨', source: '運送中',
    description: '請逐款核對實際收到的顏色、尺寸與件數。可分批收貨，未收到的數量會保留在運送中。',
  },
  return_to_office: {
    label: '移回辦公室', source: '在櫃',
    description: '將實際在辦公室的數量從在櫃移回辦公室。可用於退回辦公室，或修正先前登記為在櫃的庫存。',
  },
};
function available(row: NokeLocationRow, action: TransferAction): number {
  return action === 'dispatch' ? row.qty_office ?? 0
    : action === 'receive' ? row.qty_in_transit ?? 0 : row.qty_on_hand;
}
function newReference(): string {
  return `noke-${Date.now()}-${Array.from(crypto.getRandomValues(new Uint32Array(4))).join('-')}`;
}
function readPending(key: string): TransferRequest | null {
  try {
    const saved = JSON.parse(sessionStorage.getItem(key) || 'null');
    if (!saved || !Object.prototype.hasOwnProperty.call(actions, saved.action)
      || typeof saved.client_ref !== 'string' || !saved.client_ref
      || !Array.isArray(saved.items) || saved.items.length === 0
      || !saved.items.every((item: any) => Number.isSafeInteger(item.variant_id)
        && Number.isSafeInteger(item.qty) && item.qty > 0)) return null;
    return saved;
  } catch { return null; }
}

export default function NokeInventoryTransfer({ rows, onComplete, userId, disabled = false }: {
  rows: NokeLocationRow[];
  onComplete: () => void;
  userId: number;
  disabled?: boolean;
}): React.ReactElement {
  const storageKey = `noke-pending-transfer:${userId}`;
  const [restored] = useState(() => readPending(storageKey));
  const [action, setAction] = useState<TransferAction | null>(restored?.action ?? null);
  const [quantities, setQuantities] = useState<Record<number, string>>(() => Object.fromEntries(
    (restored?.items || []).map((item) => [item.variant_id, String(item.qty)])));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [uncertain, setUncertain] = useState(Boolean(restored));
  const [success, setSuccess] = useState('');
  const [returnMode, setReturnMode] = useState<'batch' | 'manual'>('batch');
  const [returnRows, setReturnRows] = useState<NokeLocationRow[]>([]);
  const [fetching, setFetching] = useState(false);
  const fetchVersion = useRef(0);
  const pending = useRef<TransferRequest | null>(restored);
  const submittingRef = useRef(false);
  const batchReturn = action === 'return_to_office' && returnMode === 'batch';
  const sourceRows = batchReturn ? returnRows : rows;
  const eligible = uncertain && pending.current ? pending.current.items.map((item) =>
    sourceRows.find((row) => row.variant_id === item.variant_id) || rows.find((row) => row.variant_id === item.variant_id) || {
      variant_id: item.variant_id, sku: `#${item.variant_id}`, product_title: '待核對商品', color: '', size: '',
      qty_on_hand: 0, qty_office: 0, qty_in_transit: 0,
    }) : action ? sourceRows.filter((row) => available(row, action) > 0) : [];
  const items = eligible.map((row) => ({ variant_id: row.variant_id, qty: Number(quantities[row.variant_id] || 0) }));
  const invalid = items.some((item) => !Number.isSafeInteger(item.qty) || item.qty < 0
    || item.qty > available(eligible.find((row) => row.variant_id === item.variant_id)!, action!));
  const total = items.reduce((sum, item) => sum + (Number.isFinite(item.qty) ? item.qty : 0), 0);
  const selectedCount = items.filter((item) => item.qty > 0).length;
  const loadReturnCandidates = async () => {
    const version = ++fetchVersion.current;
    setReturnMode('batch'); setReturnRows([]); setQuantities({}); setFetching(true); setError('');
    pending.current = null;
    try {
      // Fetch all inventory independently of the table's current search/filter.
      const response = await api.get('/api/noke/inventory');
      if (version !== fetchVersion.current) return;
      if (!Array.isArray(response.data.items)) throw new Error('Invalid inventory response');
      const candidates: NokeLocationRow[] = response.data.items.filter((row: NokeLocationRow) =>
        row.qty_on_hand > 0 && (row.on_display === false || !row.storage_slot?.trim()));
      setReturnRows(candidates);
      setQuantities(Object.fromEntries(candidates.map((row) => [row.variant_id, String(row.qty_on_hand)])));
    } catch (err: any) {
      if (version === fetchVersion.current) setError(err.response?.data?.message || '無法載入批次清單，請重新載入後再試');
    } finally {
      if (version === fetchVersion.current) setFetching(false);
    }
  };
  const close = () => {
    if (submitting) return;
    fetchVersion.current++; setFetching(false); setAction(null);
  };
  const open = (next: TransferAction) => {
    if (pending.current && uncertain) { setAction(pending.current.action); return; }
    fetchVersion.current++; setFetching(false); setReturnRows([]);
    setAction(next); setQuantities({}); setError(''); setSuccess(''); pending.current = null;
    if (next === 'return_to_office') void loadReturnCandidates();
  };
  const submit = async () => {
    if (!action || fetching || submittingRef.current || (!uncertain && (invalid || total <= 0 || selectedCount > 500))) return;
    const request = pending.current || { action, items: items.filter((item) => item.qty > 0), client_ref: newReference() };
    try {
      sessionStorage.setItem(storageKey, JSON.stringify(request));
    } catch {
      setError('無法儲存操作紀錄，尚未送出。請允許瀏覽器儲存資料後重試。');
      return;
    }
    pending.current = request;
    submittingRef.current = true;
    setSubmitting(true); setError('');
    try {
      await api.post('/api/noke/inventory/transfer', request);
      sessionStorage.removeItem(storageKey);
      pending.current = null;
      setUncertain(false);
      setSuccess(`${actions[request.action].label}完成，共 ${request.items.reduce((sum, item) => sum + item.qty, 0)} 件`);
      setAction(null);
      onComplete();
    } catch (err: any) {
      const status = err.response?.status;
      const unresolved = !status || status >= 500;
      setUncertain(unresolved);
      if (!unresolved) { pending.current = null; sessionStorage.removeItem(storageKey); }
      setError(err.response?.data?.message || '無法確認處理結果，請重試同一筆操作');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };
  return (
    <Box sx={{ my: 2 }}>
      <Alert severity="info" sx={{ mb: 1 }}>
        辦公室登記與列印標籤不計入在櫃庫存；送出後由專櫃確認收貨。上架狀態另外管理，補貨清單只計算在櫃數量。
      </Alert>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        {(Object.keys(actions) as TransferAction[]).map((key) => (
          <Button key={key} variant={key === 'receive' ? 'contained' : 'outlined'}
            disabled={disabled || submitting} onClick={() => open(key)}>{actions[key].label}</Button>
        ))}
      </Box>
      {success && <Alert severity="success" sx={{ mt: 1 }}>{success}</Alert>}
      <Dialog open={action !== null} onClose={close} maxWidth="md" fullWidth>
        <DialogTitle>{action ? actions[action].label : ''}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>{action ? actions[action].description : ''}</Typography>
          {action === 'return_to_office' && !uncertain && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
              <Button disabled={submitting || fetching} onClick={() => void loadReturnCandidates()}>
                {batchReturn ? '重新載入批次清單' : '自動帶入未上架／未設櫃位'}
              </Button>
              {batchReturn && <Button disabled={submitting} onClick={() => {
                fetchVersion.current++; setFetching(false); setReturnMode('manual'); setQuantities({}); setError('');
              }}>改為手動選擇</Button>}
            </Box>
          )}
          <Typography variant="caption">
            {batchReturn ? '批次清單來自全部庫存：未上架「或」未設櫃位，且在櫃數量大於 0。已預填全部在櫃件數，可取消勾選或修改件數。'
              : '以下為目前搜尋範圍內可移動的商品；未填或填 0 的款式不會移動。'}
          </Typography>
          {batchReturn && <Alert severity="info" sx={{ my: 1 }}>
            未上架或未設櫃位也可能是在店商品，請取消不需移回的款式。按下確認後才會移至辦公室，辦公室與運送中原有數量不會被扣除。
          </Alert>}
          {error && <Alert severity="error" sx={{ my: 1 }}>{error}</Alert>}
          {uncertain && <Alert severity="warning" sx={{ my: 1 }}>結果尚未確認，數量暫時鎖定。重試會核對同一筆操作，避免重複移動。</Alert>}
          {fetching ? <Alert severity="info">正在從全部庫存載入未上架或未設櫃位商品…</Alert>
            : eligible.length === 0 ? <Alert severity="info">{batchReturn ? '沒有符合條件的在櫃庫存' : `此搜尋範圍內沒有可${action ? actions[action].label : '移動'}的庫存`}</Alert> : (
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow>
                  {batchReturn && <TableCell padding="checkbox"><Checkbox
                    inputProps={{ 'aria-label': '全選批次移回商品' }}
                    checked={selectedCount === eligible.length && eligible.length > 0}
                    indeterminate={selectedCount > 0 && selectedCount < eligible.length}
                    disabled={submitting || uncertain}
                    onChange={(_, checked) => setQuantities(Object.fromEntries(eligible.map((row) =>
                      [row.variant_id, checked ? String(row.qty_on_hand) : '0'])))} /></TableCell>}
                  <TableCell>商品 / SKU</TableCell><TableCell>顏色 / 尺寸</TableCell>
                  {batchReturn && <TableCell>上架狀態 / 櫃位</TableCell>}
                  <TableCell align="right">{action ? actions[action].source : ''}可用</TableCell><TableCell>本次件數</TableCell></TableRow></TableHead>
                <TableBody>{eligible.map((row) => (
                  <TableRow key={row.variant_id}>
                    {batchReturn && <TableCell padding="checkbox"><Checkbox
                      inputProps={{ 'aria-label': `${row.sku} ${row.color} ${row.size} 移回辦公室` }}
                      checked={Number(quantities[row.variant_id] || 0) > 0}
                      disabled={submitting || uncertain}
                      onChange={(_, checked) => setQuantities((prev) => ({ ...prev,
                        [row.variant_id]: checked ? String(row.qty_on_hand) : '0' }))} /></TableCell>}
                    <TableCell>{row.product_title}<Typography variant="caption" display="block">{row.sku}</Typography></TableCell>
                    <TableCell>{row.color || '—'} / {row.size || '—'}</TableCell>
                    {batchReturn && <TableCell>{row.on_display === false ? '未上架' : row.on_display === true ? '已上架' : '上架狀態未記錄'}<br />
                      {row.storage_slot?.trim() || '未設櫃位'}</TableCell>}
                    <TableCell align="right">{available(row, action!)}</TableCell>
                    <TableCell><TextField type="number" size="small" value={quantities[row.variant_id] || ''}
                      disabled={submitting || uncertain} placeholder="0" sx={{ width: 110 }}
                      inputProps={{ min: 0, max: available(row, action!), step: 1,
                        'aria-label': `${row.sku} ${row.color} ${row.size} 本次件數` }}
                      onChange={(event) => {
                        setQuantities((prev) => ({ ...prev, [row.variant_id]: event.target.value }));
                        pending.current = null;
                      }} /></TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </Box>
          )}
          {invalid && !uncertain && <Alert severity="error">件數須為整數，且不可超過來源可用數量。</Alert>}
          {selectedCount > 500 && !uncertain && <Alert severity="error">每次最多移動 500 個款式，請取消部分勾選後分批處理。</Alert>}
          {!fetching && <Typography sx={{ mt: 2 }}>已選 {selectedCount} 個款式，共 {total} 件</Typography>}
        </DialogContent>
        <DialogActions>
          <Button disabled={submitting} onClick={close}>關閉</Button>
          <Button variant="contained" onClick={submit} disabled={submitting || fetching || (!uncertain && (invalid || total <= 0 || selectedCount > 500))}>
            {submitting ? '處理中…' : uncertain ? '重試同一筆操作' : `${action === 'receive' ? '確認收貨' : `確認${action ? actions[action].label : ''}`} (${total} 件)`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
